import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, MapPin, Clock, Phone } from "lucide-react";

export default function ContactUs() {
  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-secondary mb-4">Contact Us</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We're here to help! Reach out to us for any questions, support, or feedback.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Information */}
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-secondary">Get in Touch</CardTitle>
              <CardDescription>
                Our team is always ready to assist you with your career journey.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Urgent Contacts */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-secondary mb-1">Urgent Contact</h3>
                  <p className="text-gray-600 mb-2">For any urgent matters, contact our team leads</p>
                  <div className="space-y-3">
                    <div>
                      <p className="text-gray-800 font-medium">Mr. Balakrishna Kale</p>
                      <a 
                        href="tel:+918179101888" 
                        className="text-primary hover:text-green-700 font-medium transition-colors"
                      >
                        +91 81791 01888
                      </a>
                    </div>
                    <div>
                      <p className="text-gray-800 font-medium">Mr. Srinath Achar</p>
                      <a 
                        href="tel:+919848047074" 
                        className="text-primary hover:text-green-700 font-medium transition-colors"
                      >
                        +91 98480 47074
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-secondary mb-1">Email</h3>
                  <p className="text-gray-600 mb-2">Send us an email and we'll respond within 24 hours</p>
                  <a 
                    href="mailto:info@udyogapramoda.org" 
                    className="text-primary hover:text-green-700 font-medium transition-colors"
                  >
                    info@udyogapramoda.org
                  </a>
                </div>
              </div>

              {/* Response Time */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-secondary mb-1">Response Time</h3>
                  <p className="text-gray-600">
                    We typically respond to all inquiries within 24 hours during business days.
                  </p>
                </div>
              </div>

              {/* Office Location */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-secondary mb-1">Location</h3>
                  <p className="text-gray-600">
                    Hyderabad, Telangana, India
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-secondary">Send us a Message</CardTitle>
              <CardDescription>
                Fill out the form below and we'll get back to you as soon as possible.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <Mail className="w-16 h-16 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  Email us directly
                </h3>
                <p className="text-green-800 mb-4">
                  For the fastest response, please send your message directly to our email address.
                </p>
                <a 
                  href="mailto:info@udyogapramoda.org?subject=Contact%20from%20Website"
                  className="inline-flex items-center px-6 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Send Email
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Information */}
        <div className="mt-12 text-center">
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-none">
            <CardContent className="py-8">
              <h3 className="text-2xl font-bold text-secondary mb-4">
                About Udyoga Pramoda
              </h3>
              <p className="text-gray-600 max-w-3xl mx-auto mb-6">
                Udyoga Pramoda is dedicated to empowering careers and building futures. 
                We connect job aspirants with experienced mentors and provide comprehensive 
                training opportunities to accelerate professional growth.
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                <span>🚀 Career Development</span>
                <span>👥 Mentor Network</span>
                <span>📚 Training Programs</span>
                <span>💼 Job Opportunities</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 